import { apiGet, apiSend } from './client';
import type {
  AdminRaidContent,
  ExpansionRecord,
  RaidBossRecord,
  RaidObjectiveRecord,
  RaidTierRecord,
  RaidZoneRecord,
  RankingPartitionRecord
} from './types';

export interface ExpansionPayload {
  slug: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export interface RaidZonePayload {
  expansionId: string;
  raidTierId?: string;
  serverId?: number | null;
  slug: string;
  name: string;
  shortName?: string | null;
  aliases: string[];
  sortOrder: number;
  isActive: boolean;
}

export interface RaidBossPayload {
  raidZoneId: string;
  raidTierId?: string;
  slug: string;
  name: string;
  encounterLogName?: string | null;
  encounterNameAliases: string[];
  sortOrder: number;
  isActive: boolean;
  rankingEnabled: boolean;
  isProgressionRelevant: boolean;
  isLeaderboardRelevant: boolean;
  activeFromExpansionId?: string | null;
  activeUntilExpansionId?: string | null;
  supersedesBossId?: string | null;
}

export interface RaidObjectivePayload {
  expansionId: string;
  raidTierId?: string;
  slug: string;
  name: string;
  description?: string | null;
  completionMode: 'ALL_REQUIRED' | 'REQUIRED_COUNT';
  requiredCount?: number | null;
  sortOrder: number;
  isActive: boolean;
  isProgressionRelevant: boolean;
  bossIds: string[];
}

export interface RankingPartitionPayload {
  raidTierId?: string;
  expansionId?: string;
  anchorExpansionId?: string | null;
  expansionIds?: string[];
  slug: string;
  name: string;
  startsAt?: string | null;
  endsAt?: string | null;
  isFrozen: boolean;
  isDefault: boolean;
}

export async function fetchAdminRaidContent() {
  return apiGet<AdminRaidContent>('/admin/raid-content');
}

export async function createExpansion(payload: ExpansionPayload) {
  const data = await apiSend<{ expansion: ExpansionRecord }>('/admin/raid-content/expansions', 'POST', payload);
  return data.expansion;
}

export async function updateExpansion(id: string, payload: Partial<ExpansionPayload>) {
  const data = await apiSend<{ expansion: ExpansionRecord }>(`/admin/raid-content/expansions/${encodeURIComponent(id)}`, 'PUT', payload);
  return data.expansion;
}

export async function deleteExpansion(id: string) {
  await apiSend<unknown>(`/admin/raid-content/expansions/${encodeURIComponent(id)}`, 'DELETE');
}

export async function createRaidZone(payload: RaidZonePayload) {
  const data = await apiSend<{ raidZone: RaidZoneRecord }>('/admin/raid-content/zones', 'POST', payload);
  return data.raidZone;
}

export async function updateRaidZone(id: string, payload: Partial<RaidZonePayload>) {
  const data = await apiSend<{ raidZone: RaidZoneRecord }>(`/admin/raid-content/zones/${encodeURIComponent(id)}`, 'PUT', payload);
  return data.raidZone;
}

export async function deleteRaidZone(id: string) {
  await apiSend<unknown>(`/admin/raid-content/zones/${encodeURIComponent(id)}`, 'DELETE');
}

export async function createRaidBoss(payload: RaidBossPayload) {
  const data = await apiSend<{ raidBoss: RaidBossRecord }>('/admin/raid-content/bosses', 'POST', payload);
  return data.raidBoss;
}

export async function updateRaidBoss(id: string, payload: Partial<RaidBossPayload>) {
  const data = await apiSend<{ raidBoss: RaidBossRecord }>(`/admin/raid-content/bosses/${encodeURIComponent(id)}`, 'PUT', payload);
  return data.raidBoss;
}

export async function deleteRaidBoss(id: string) {
  await apiSend<unknown>(`/admin/raid-content/bosses/${encodeURIComponent(id)}`, 'DELETE');
}

export async function createRaidObjective(payload: RaidObjectivePayload) {
  const data = await apiSend<{ raidObjective: RaidObjectiveRecord }>('/admin/raid-content/objectives', 'POST', payload);
  return data.raidObjective;
}

export async function updateRaidObjective(id: string, payload: Partial<RaidObjectivePayload>) {
  const data = await apiSend<{ raidObjective: RaidObjectiveRecord }>(`/admin/raid-content/objectives/${encodeURIComponent(id)}`, 'PUT', payload);
  return data.raidObjective;
}

export async function replaceRaidObjectiveBosses(id: string, bossIds: string[]) {
  const data = await apiSend<{ raidObjective: RaidObjectiveRecord }>(`/admin/raid-content/objectives/${encodeURIComponent(id)}/bosses`, 'PUT', { bossIds });
  return data.raidObjective;
}

export async function deleteRaidObjective(id: string) {
  await apiSend<unknown>(`/admin/raid-content/objectives/${encodeURIComponent(id)}`, 'DELETE');
}

export async function createRankingPartition(payload: RankingPartitionPayload) {
  const data = await apiSend<{ rankingPartition: RankingPartitionRecord }>('/admin/raid-content/partitions', 'POST', payload);
  return data.rankingPartition;
}

export async function updateRankingPartition(id: string, payload: Partial<RankingPartitionPayload>) {
  const data = await apiSend<{ rankingPartition: RankingPartitionRecord }>(`/admin/raid-content/partitions/${encodeURIComponent(id)}`, 'PUT', payload);
  return data.rankingPartition;
}

export async function deleteRankingPartition(id: string) {
  await apiSend<unknown>(`/admin/raid-content/partitions/${encodeURIComponent(id)}`, 'DELETE');
}

export interface RaidTierPayload {
  expansionId: string;
  slug: string;
  name: string;
  sortOrder: number;
  isActive: boolean;
}

export async function createRaidTier(payload: RaidTierPayload) {
  const data = await apiSend<{ raidTier: RaidTierRecord }>('/admin/raid-content/tiers', 'POST', payload);
  return data.raidTier;
}

export async function updateRaidTier(id: string, payload: Partial<RaidTierPayload>) {
  const data = await apiSend<{ raidTier: RaidTierRecord }>(`/admin/raid-content/tiers/${encodeURIComponent(id)}`, 'PUT', payload);
  return data.raidTier;
}

export async function deleteRaidTier(id: string) {
  await apiSend<unknown>(`/admin/raid-content/tiers/${encodeURIComponent(id)}`, 'DELETE');
}
